import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Grid,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import axios from "axios";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import AuthorityTransactionTable from "../../components/Fleet/AuthorityTransactionTable/AuthorityTransactionTable";
import { fetchOperationsPlannerSummary } from "../../api/operationsPlannerSummaryService";
import useAuth from "../../hooks/useAuth";
import {
  OperationsPlannerSummaryDto,
  OperationsPlannerSummaryItemDto,
} from "../../interfaces/Operations/operationsPlannerSummary";
import { apiVariable } from "../../api/apiVariable";
import { Company, getCompanies } from "../../api/companyService";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
);

interface DistributionBucketDto {
  label: string;
  count: number;
  percentage: number;
}

interface FleetTruckStatisticsDto {
  totalUnits: number;
  fleetAgeDistribution: DistributionBucketDto[];
  odometerDistribution: DistributionBucketDto[];
  makeMix: DistributionBucketDto[];
  medianAgeYears: number;
  averageAgeYears: number;
  averageOdometerMiles: number;
  unitsOver500kMilesPercentage: number;
  preventiveMaintenanceOverdueCount: number;
}

const fetchTotalTrucksCountByType = async (
  token: string | null | undefined,
) => {
  const { data } = await axios.get(`${apiVariable}/totalTrucksCountByType`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

const fetchFleetTruckStatistics = async (
  token: string | null | undefined,
  companyId?: string,
  filterByAuthorityCompanyId = false,
): Promise<FleetTruckStatisticsDto> => {
  const { data } = await axios.get<FleetTruckStatisticsDto>(
    `${apiVariable}/fleetTruckStatistics`,
    {
      params: {
        companyId: companyId || undefined,
        filterByAuthorityCompanyId,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return data;
};

const RentalHomePage = () => {
  const { token } = useAuth();
  const [filterByAuthorityCompany, setFilterByAuthorityCompany] =
    useState(false);
  const [selectedOwnerCompany, setSelectedOwnerCompany] =
    useState<Company | null>(null);

  const selectedDateKey = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const {
    data: summary,
    isLoading,
    isError,
    error,
  } = useQuery<OperationsPlannerSummaryDto, Error>({
    queryKey: ["rental-dashboard-summary", selectedDateKey],
    queryFn: () => fetchOperationsPlannerSummary(token, selectedDateKey),
    enabled: Boolean(token),
  });

  const {
    data: totalTrucksCountByType,
    isLoading: isLoadingTotalTrucksCountByType,
  } = useQuery({
    queryKey: ["totalTrucksCountByType", "rental-dashboard"],
    queryFn: () => fetchTotalTrucksCountByType(token),
    enabled: Boolean(token),
  });

  const { data: fleetTruckStatistics, isLoading: isLoadingFleetStatistics } =
    useQuery({
      queryKey: [
        "fleetTruckStatistics",
        "rental-dashboard",
        selectedOwnerCompany?.id ?? "all-owner-companies",
        filterByAuthorityCompany,
      ],
      queryFn: () =>
        fetchFleetTruckStatistics(
          token,
          selectedOwnerCompany?.id,
          filterByAuthorityCompany,
        ),
      enabled: Boolean(token),
    });

  const { data: ownerCompanies = [], isLoading: isLoadingOwnerCompanies } =
    useQuery({
      queryKey: ["ownerCompanies", "rental-dashboard"],
      queryFn: () => getCompanies(token),
      enabled: Boolean(token),
    });

  const allSummaryItems: OperationsPlannerSummaryItemDto[] = useMemo(
    () => summary?.sections.flatMap((s) => s.items) ?? [],
    [summary],
  );

  const operationsMetricKeys = useMemo(
    () => [
      "metric-total-trucks",
      "metric-for-sale",
      "metric-rent-out",
      "metric-lease-out",
    ],
    [],
  );

  const soldMetric = useMemo(
    () => allSummaryItems.find((i) => i.key === "metric-sold"),
    [allSummaryItems],
  );

  const createDistributionBarData = (data: DistributionBucketDto[] = []) => ({
    labels: data.map((bucket) => bucket.label),
    datasets: [
      {
        label: "Units",
        data: data.map((bucket) => bucket.count),
        backgroundColor: ["#648f70", "#4f7a5d", "#8aae82", "#b3c9a2"],
        borderRadius: 6,
      },
    ],
  });

  const createBarChartOptions = (data: DistributionBucketDto[] = []) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (ctx: { dataIndex: number; parsed: { y: number } }) => {
            const percentage = data[ctx.dataIndex]?.percentage ?? 0;
            return `${ctx.parsed.y} units (${percentage.toFixed(1)}%)`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  });

  const openInNewTab = (path: string) => {
    window.open(path, "_blank", "noopener,noreferrer");
  };

  const getFleetStatisticsDrilldownParams = (): Record<
    string,
    string | undefined
  > => ({
    excludeStatuses: "PERSONAL,PLACEHOLDER",
    companyId: selectedOwnerCompany?.id,
    filterByAuthorityCompanyId:
      selectedOwnerCompany && filterByAuthorityCompany ? "true" : undefined,
  });

  const toTruckAssetsPage = (params?: Record<string, string | undefined>) => {
    const searchParams = new URLSearchParams();
    searchParams.set("tab", "1");

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          searchParams.set(key, value);
        }
      });
    }

    openInNewTab(`/fleet/assets?${searchParams.toString()}`);
  };

  const toMaintenancePage = (operationPerformed: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set("tab", "2");
    searchParams.set("operationPerformed", operationPerformed);
    // Only show overdue PMs (exclude "overdue soon") when navigating
    // from the rental dashboard's Preventive Maintenance card.
    searchParams.set("maintenanceStatus", "overdue");
    openInNewTab(`/fleet/assets?${searchParams.toString()}`);
  };

  const makeMixData = {
    labels: fleetTruckStatistics?.makeMix.map((bucket) => bucket.label) ?? [],
    datasets: [
      {
        data: fleetTruckStatistics?.makeMix.map((bucket) => bucket.count) ?? [],
        backgroundColor: [
          "#6d8b74",
          "#9db59d",
          "#f0c690",
          "#ea9b7e",
          "#a7b4d8",
          "#b4a7c9",
        ],
        borderWidth: 1,
      },
    ],
  };

  return (
    <Box sx={{ p: 2 }}>
      {isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error?.message || "Unable to load additional metrics."}
        </Alert>
      )}

      <Card sx={{ boxShadow: 3, borderRadius: 2, mb: 2 }}>
        <CardHeader className="responsive-card-header" title="Metrics" />
        <CardContent>
          {isLoading && isLoadingTotalTrucksCountByType ? (
            <Box
              flexWrap={{ xs: "wrap", lg: "nowrap" }}
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={3}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {operationsMetricKeys.map((key) => {
                const item = allSummaryItems.find((i) => i.key === key);

                if (!item) {
                  return null;
                }

                const value =
                  item.actualSeated ?? item.projected ?? item.difference ?? 0;

                return (
                  <Grid item xs={12} sm={6} md={3} key={item.key}>
                    <Box
                      p={2}
                      border={1}
                      borderColor="divider"
                      borderRadius={2}
                      height="100%"
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        if (item.key === "metric-total-trucks") {
                          toTruckAssetsPage();
                          return;
                        }
                        if (item.key === "metric-for-sale") {
                          toTruckAssetsPage({ status: "FOR SALE" });
                          return;
                        }
                        if (item.key === "metric-rent-out") {
                          toTruckAssetsPage({ transactionType: "RENT OUT" });
                          return;
                        }
                        if (item.key === "metric-lease-out") {
                          toTruckAssetsPage({ transactionType: "LEASE OUT" });
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        textTransform="uppercase"
                        gutterBottom
                        display="block"
                      >
                        {item.label}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {value}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}

              {totalTrucksCountByType &&
                totalTrucksCountByType.length > 0 &&
                (() => {
                  const findByType = (match: string) =>
                    totalTrucksCountByType.find(
                      (item: any) =>
                        typeof item.transactionType === "string" &&
                        item.transactionType
                          .toLowerCase()
                          .includes(match.toLowerCase()),
                    );

                  const available = findByType("available");
                  const repairs = findByType("repair");

                  const tiles: JSX.Element[] = [];

                  if (available) {
                    tiles.push(
                      <Grid item xs={12} sm={6} md={3} key="trucks-available">
                        <Box
                          p={2}
                          border={1}
                          borderColor="divider"
                          borderRadius={2}
                          height="100%"
                          sx={{ cursor: "pointer" }}
                          onClick={() =>
                            toTruckAssetsPage({ status: "AVAILABLE" })
                          }
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            textTransform="uppercase"
                            gutterBottom
                            display="block"
                          >
                            Available trucks
                          </Typography>
                          <Typography variant="h5" fontWeight={700}>
                            {available.totalAssets}
                          </Typography>
                        </Box>
                      </Grid>,
                    );
                  }

                  if (repairs) {
                    tiles.push(
                      <Grid item xs={12} sm={6} md={3} key="trucks-repairs">
                        <Box
                          p={2}
                          border={1}
                          borderColor="divider"
                          borderRadius={2}
                          height="100%"
                          sx={{ cursor: "pointer" }}
                          onClick={() =>
                            toTruckAssetsPage({ status: "REPAIR" })
                          }
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            textTransform="uppercase"
                            gutterBottom
                            display="block"
                          >
                            Trucks in repair
                          </Typography>
                          <Typography variant="h5" fontWeight={700}>
                            {repairs.totalAssets}
                          </Typography>
                        </Box>
                      </Grid>,
                    );
                  }

                  return tiles;
                })()}

              {soldMetric && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} key={soldMetric.key}>
                    <Box
                      p={2}
                      border={1}
                      borderColor="divider"
                      borderRadius={2}
                      height="100%"
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        toTruckAssetsPage({
                          status: "SOLD",
                          areActive: "false",
                        })
                      }
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        textTransform="uppercase"
                        gutterBottom
                        display="block"
                      >
                        {soldMetric.label}
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {soldMetric.actualSeated ??
                          soldMetric.projected ??
                          soldMetric.difference ??
                          0}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: 3, borderRadius: 2, mb: 2 }}>
        <CardHeader
          className="responsive-card-header"
          title="Fleet Truck Statistics"
        />
        <CardContent>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterByAuthorityCompany}
                    onChange={(_, checked) =>
                      setFilterByAuthorityCompany(checked)
                    }
                  />
                }
                label="Filter by authority company"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Autocomplete
                fullWidth
                options={ownerCompanies}
                loading={isLoadingOwnerCompanies}
                value={selectedOwnerCompany}
                onChange={(_, company) => setSelectedOwnerCompany(company)}
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Owner company"
                    placeholder="Select owner company"
                  />
                )}
              />
            </Grid>
          </Grid>

          {isLoadingFleetStatistics ? (
            <Box
              flexWrap={{ xs: "wrap", lg: "nowrap" }}
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={3}
            >
              <CircularProgress size={24} />
            </Box>
          ) : !fleetTruckStatistics ? (
            <Alert severity="info">No fleet statistics available.</Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={2}>
                <Box
                  p={2}
                  border={1}
                  borderColor="divider"
                  borderRadius={2}
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    toTruckAssetsPage({
                      ...getFleetStatisticsDrilldownParams(),
                    })
                  }
                >
                  <Typography variant="caption" color="text.secondary">
                    Total Trucks
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {fleetTruckStatistics.totalUnits}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={2}>
                <Box p={2} border={1} borderColor="divider" borderRadius={2}>
                  <Typography variant="caption" color="text.secondary">
                    Avg Age
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {fleetTruckStatistics.averageAgeYears.toFixed(1)} yrs
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={2}>
                <Box p={2} border={1} borderColor="divider" borderRadius={2}>
                  <Typography variant="caption" color="text.secondary">
                    Median Age
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {fleetTruckStatistics.medianAgeYears.toFixed(1)} yrs
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={2}>
                <Box p={2} border={1} borderColor="divider" borderRadius={2}>
                  <Typography variant="caption" color="text.secondary">
                    Avg Odometer
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {fleetTruckStatistics.averageOdometerMiles.toLocaleString()}{" "}
                    mi
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={2}>
                <Box p={2} border={1} borderColor="divider" borderRadius={2}>
                  <Typography variant="caption" color="text.secondary">
                    {"% Over 500k mi"}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {fleetTruckStatistics.unitsOver500kMilesPercentage.toFixed(
                      1,
                    )}
                    %
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardHeader
                    className="responsive-card-header"
                    title="Fleet Age Distribution"
                  />
                  <CardContent sx={{ height: 280 }}>
                    <Bar
                      data={createDistributionBarData(
                        fleetTruckStatistics.fleetAgeDistribution,
                      )}
                      options={{
                        ...createBarChartOptions(
                          fleetTruckStatistics.fleetAgeDistribution,
                        ),
                        onClick: (_event, elements) => {
                          if (!elements.length) return;
                          const clickedBucket =
                            fleetTruckStatistics.fleetAgeDistribution[
                              elements[0].index
                            ];
                          if (!clickedBucket) return;
                          toTruckAssetsPage({
                            ...getFleetStatisticsDrilldownParams(),
                            ageBucket: clickedBucket.label,
                          });
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardHeader
                    className="responsive-card-header"
                    title="Odometer Distribution"
                  />
                  <CardContent sx={{ height: 280 }}>
                    <Bar
                      data={createDistributionBarData(
                        fleetTruckStatistics.odometerDistribution,
                      )}
                      options={{
                        ...createBarChartOptions(
                          fleetTruckStatistics.odometerDistribution,
                        ),
                        onClick: (_event, elements) => {
                          if (!elements.length) return;
                          const clickedBucket =
                            fleetTruckStatistics.odometerDistribution[
                              elements[0].index
                            ];
                          if (!clickedBucket) return;
                          toTruckAssetsPage({
                            ...getFleetStatisticsDrilldownParams(),
                            odometerBucket: clickedBucket.label,
                          });
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardHeader
                    className="responsive-card-header"
                    title="Make Mix"
                  />
                  <CardContent
                    sx={{
                      height: 280,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Doughnut
                      data={makeMixData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: "right",
                          },
                        },
                        onClick: (_event, elements) => {
                          if (!elements.length) return;
                          const clickedBucket =
                            fleetTruckStatistics.makeMix[elements[0].index];
                          if (!clickedBucket) return;
                          toTruckAssetsPage({
                            ...getFleetStatisticsDrilldownParams(),
                            make: clickedBucket.label,
                          });
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Box
                  p={2}
                  border={1}
                  borderColor="divider"
                  borderRadius={2}
                  sx={{ cursor: "pointer" }}
                  onClick={() => toMaintenancePage("PMOILCHANGE")}
                >
                  <Typography variant="body2" color="text.secondary">
                    Preventive Maintenance Overdue
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {fleetTruckStatistics.preventiveMaintenanceOverdueCount}{" "}
                    units
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardHeader
          className="responsive-card-header"
          title="Authority Transactions"
        />
        <CardContent
          sx={{
            overflow: "auto",
            p: 0,
            maxHeight: 780,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AuthorityTransactionTable containerHeight={760} gridHeight={560} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default RentalHomePage;
